import { APP_INITIALIZER, ApplicationConfig,  Injector } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { API_URL, Common, NotificationWebSocketService, provideNotification, provideTranslation, Workflow_URL } from '@techextensor/tab-core-utility';
import { AuthedNotificationWebSocketService } from './core/services/notification/authed-notification-websocket.service';
import { provideAuth } from './core/providers/auth.provider';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CacheInterceptor } from './core/interceptors/cache.interceptor';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { ApiStatusInterceptor } from './core/interceptors/api-status.interceptor'; // Update the path as needed
import { environment } from '../environments/environment';
import { FormioInterceptorService } from './core/interceptors/formio.interceptor';
import { TabSdk } from '@techextensor/tab-sdk';
import { HelperFunctionService } from './core/services/helper-function.service';
import { EventActionService } from './core/services/event-action.service';
import { OrgAppInfoHelperService } from './core/services/org-app-info-helper.service';
import { CustomComponentLogService } from './core/services/formio/custom-component-log.service';
import { ApiStatusService } from './core/services/api-status.service';
import { ScreenNavigationMappingService } from './core/services/screen-navigation-mapping.service';
import { FavFilterService } from './core/services/formio/fav-filter.service';
import { UserFavoritesService } from './core/services/user-favorites.service';
import { FormioCacheInterceptor } from './core/interceptors/formio-cache.interceptor';
import { PermissionService } from './core/services/permission.service';
import { CustomComponentRegisterService } from './core/services/formio/custom-component-register.service';
import { FormioAppConfig } from '@formio/angular';
import { timelineHttpInterceptor } from './core/interceptors/timeline-http.interceptor';
import { StateService } from './core/services/state-management/state.service';
import { HttpInterceptorFn } from '@angular/common/http';
import { ConsoleDataSource } from './core/services/dev-tool/console-data-source';
import { TimelineDataSource } from './core/services/dev-tool/timeline-data-source';
import { DebugLoggerService } from './core/services/dev-tool/debug-logger.service';
import { TimelineTrackerService } from './core/services/dev-tool/timeline-tracker.service';
import { PreviewEnvironmentService } from './core/services/preview-environment.service';

export function initApp(formioInterceptor: FormioInterceptorService, injector: Injector) {
  return async () => {
    Common.init(environment);
    TabSdk.init(injector,{
      HelperFunctionService: injector.get(HelperFunctionService),
      EventActionService: injector.get(EventActionService),
      OrgAppInfoHelperService: injector.get(OrgAppInfoHelperService),
      CustomComponentLogService: injector.get(CustomComponentLogService),
      ApiStatusService: injector.get(ApiStatusService),
      ScreenNavigationMappingService: injector.get(ScreenNavigationMappingService),
      FavFilterService: injector.get(FavFilterService),
      UserFavoritesService: injector.get(UserFavoritesService),
      PermissionService: injector.get(PermissionService),
      CustomComponentRegisterService: injector.get(CustomComponentRegisterService),
      StateService: injector.get(StateService)
    });
    formioInterceptor.intercept();
    // Before the router runs: read ?environmentId= (TAF Studio "Open preview") and take it out of the address.
    injector.get(PreviewEnvironmentService).capture();
  }
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()), // HTTP client with interceptors
    provideAuth(),
    provideNotification({ positionClass: 'toast-bottom-right' }),
    provideTranslation(),
    // DevTool timeline interceptor — only active when DevTool is enabled (dev environment)
    ...(environment.isDevToolEnabled
      ? [provideHttpClient(withInterceptors([timelineHttpInterceptor as HttpInterceptorFn]))]
      : []),

    // Init App
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [FormioInterceptorService, Injector, HelperFunctionService],
      multi: true
    },

    // API Configurations
    {
      provide: API_URL,
      useValue: environment.apiUrl
    },
    {
      provide: Workflow_URL,
      useValue: environment.Workflow_URL
    },

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true // Allow multiple interceptors
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true // Allow multiple interceptors
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiStatusInterceptor, // Add the ApiInterceptor here
      multi: true // Allow multiple interceptors
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FormioCacheInterceptor, // Add the ApiInterceptor here
      multi: true // Allow multiple interceptors
    },

    // Notification bell transport: same as tab-core-utility's, but sends the access token as the
    // `access_token` WebSocket subprotocol so the server can bind the socket to the token's user.
    { provide: NotificationWebSocketService, useClass: AuthedNotificationWebSocketService },

    // DevTool abstract → concrete bindings (main app uses real services)
    { provide: ConsoleDataSource, useExisting: DebugLoggerService },
    { provide: TimelineDataSource, useExisting: TimelineTrackerService },

    // Formio Configuration
    {
      provide: FormioAppConfig,
      useValue: {
        apiUrl: environment.apiUrl,
        appUrl: environment.apiUrl
      }
    }
  ]
};
